import React, { useEffect, useState } from 'react';
import { useFetch } from '../hooks/useFetch';
import { Selector, SelectorProps } from './Selector';

interface BranchSelectorProps<T> extends Omit<SelectorProps, 'options'> {
  handleChange: (value: T[] | undefined) => void;
  url: string;
  params?: Record<string, any>;
  noSelectedLabel?: string;
  labelProperty: keyof T;
  idProperty: keyof T & string;
  value?: string | string[];
  // Opciones fijas que se agregan a las que trae el endpoint, sin esperar a que
  // resuelva el fetch (útiles para comercios/sucursales de prueba que no vienen del
  // BFF real).
  staticOptions?: T[];
}

// Some BFF list endpoints respond with a bare array, others wrap it as { content: [...] }.
// Normalize both shapes here rather than assuming one, since this is an external API boundary.
const toItems = <T,>(raw: unknown): T[] => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw && typeof raw === 'object' && Array.isArray((raw as { content?: unknown }).content)) {
    return (raw as { content: T[] }).content;
  }
  return [];
};

export const AsyncSelector = <T,>(props: BranchSelectorProps<T>) => {
  const { handleChange, url, params, noSelectedLabel, labelProperty, value, idProperty, staticOptions, ...selectProps } = props;
  const { data, error, loading } = useFetch<T[]>(url, params);
  const items = [...toItems<T>(data), ...(staticOptions ?? [])];
  const [internalState, setInternalState] = useState<string | string[]>(value || []);

  useEffect(() => {
    if (selectProps.isMulti) {
      const selected = items.filter(element => value?.includes(element[idProperty] as string));
      setInternalState(selected.map((item) => item[idProperty] as string));
    } else {
      const selected = items.find(element => element[idProperty] === value);

      if (selected) {
        setInternalState(selected[idProperty] as string);
      }
    }

  }, [value, data, idProperty]);

  if (error && !loading) {
    return (
      <Selector options={[]} placeholder="Error cargado datos" />
    );
  }

  const select = (e: string | string[]) => {
    setInternalState(e);

    if (Array.isArray(e)) {
      handleChange(items.filter(element => e.includes(element[idProperty] as string)));
    } else {
      const selected = items.find(element => element[idProperty] === e);
      handleChange(selected ? [selected] : undefined);
    }
  };

  return (<>

    <Selector
      isLoading={loading}
      options={items.map(item => ({ value: item[idProperty] as string, label: item[labelProperty] as string }))}
      value={internalState}
      onChange={(e) => select(e)}
      {...selectProps}
    />
  </>);
};
